import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MultiImageUploader from "@/components/MultiImageUploader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AdminCharacters = () => {
  const [characters, setCharacters] = useState([]);
  const [universes, setUniverses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    universe_id: "",
    description: "",
    image: "",
    species: "",
    affiliation: "",
    abilities: [],
  });
  const [abilitiesInput, setAbilitiesInput] = useState("");

  const fetchData = async () => {
    try {
      const [charsRes, unisRes] = await Promise.all([
        axios.get(`${API}/characters`),
        axios.get(`${API}/universes`),
      ]);
      setCharacters(charsRes.data);
      setUniverses(unisRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      abilities: abilitiesInput.split(",").map(a => a.trim()).filter(Boolean),
    };
    
    try {
      if (editingCharacter) {
        await axios.put(`${API}/characters/${editingCharacter.id}`, submitData);
        toast.success("Character updated");
      } else {
        await axios.post(`${API}/characters`, submitData);
        toast.success("Character created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this character?")) return;
    try {
      await axios.delete(`${API}/characters/${id}`);
      toast.success("Character deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete character");
    }
  };

  const openEditDialog = (character) => {
    setEditingCharacter(character);
    setFormData({
      name: character.name,
      universe_id: character.universe_id,
      description: character.description,
      image: character.image || "",
      images: character.images || [],
      species: character.species || "",
      affiliation: character.affiliation || "",
      abilities: character.abilities || [],
    });
    setAbilitiesInput((character.abilities || []).join(", "));
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCharacter(null);
    setFormData({
      name: "",
      universe_id: "",
      description: "",
      image: "",
      images: [],
      species: "",
      affiliation: "",
      abilities: [],
    });
    setAbilitiesInput("");
  };

  const getUniverseName = (id) => {
    const uni = universes.find(u => u.id === id);
    return uni ? uni.name : "Unknown";
  };

  return (
    <AdminLayout title="Manage Characters">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-400">{characters.length} characters</p>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-character-btn"
        >
          <Plus className="w-4 h-4" />
          Add Character
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="card-surface overflow-hidden">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Character</th>
                <th>Universe</th>
                <th>Species</th>
                <th>Affiliation</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {characters.map((character) => (
                <tr key={character.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <img
                        src={character.image || "https://static.prod-images.emergentagent.com/jobs/f60715c2-3b6b-4c99-a6c8-5c96b98e6cf9/images/c87d923edbc4557b647ae3b5b7af92b6c1f0325cd0bb5d5d8bdfa7f98e780e7d.png"}
                        alt={character.name}
                        className="w-10 h-10 object-cover"
                      />
                      <span className="font-medium text-white">{character.name}</span>
                    </div>
                  </td>
                  <td className="text-slate-400">{getUniverseName(character.universe_id)}</td>
                  <td className="text-slate-400">{character.species || "-"}</td>
                  <td className="text-slate-400">{character.affiliation || "-"}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(character)}
                        data-testid={`edit-character-${character.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(character.id)}
                        className="hover:text-red-500"
                        data-testid={`delete-character-${character.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0B0F19] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">
              {editingCharacter ? "Edit Character" : "Add New Character"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="character-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark mt-1"
                  required
                  data-testid="character-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Universe</Label>
                <Select
                  value={formData.universe_id}
                  onValueChange={(value) => setFormData({ ...formData, universe_id: value })}
                >
                  <SelectTrigger className="input-dark mt-1" data-testid="character-universe">
                    <SelectValue placeholder="Select universe" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    {universes.map((uni) => (
                      <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-dark mt-1 min-h-[100px]"
                required
                data-testid="character-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Species</Label>
                <Input
                  value={formData.species}
                  onChange={(e) => setFormData({ ...formData, species: e.target.value })}
                  className="input-dark mt-1"
                  placeholder="e.g., Human, Vulcan"
                  data-testid="character-species"
                />
              </div>
              <div>
                <Label className="text-slate-300">Affiliation</Label>
                <Input
                  value={formData.affiliation}
                  onChange={(e) => setFormData({ ...formData, affiliation: e.target.value })}
                  className="input-dark mt-1"
                  placeholder="e.g., Starfleet, Jedi Order"
                  data-testid="character-affiliation"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Main Image URL (or upload below)</Label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="input-dark mt-1"
                placeholder="https://..."
                data-testid="character-image"
              />
            </div>

            <div>
              <Label className="text-slate-300">Gallery Images</Label>
              <div className="mt-1">
                <MultiImageUploader
                  images={formData.images}
                  onChange={(imgs) => setFormData({ ...formData, images: imgs, image: imgs[0] || formData.image })}
                  maxImages={8}
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Abilities (comma-separated)</Label>
              <Input
                value={abilitiesInput}
                onChange={(e) => setAbilitiesInput(e.target.value)}
                className="input-dark mt-1"
                placeholder="e.g., Super strength, Flight, Telepathy"
                data-testid="character-abilities"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="character-submit">
                {editingCharacter ? "Update" : "Create"} Character
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCharacters;
